import * as React from 'react';
import { useState, useMemo } from 'react';
import Msg from '../../../localization/Msg';
import { Str } from '../../../localization/translationKeys';
import { FLEX_ROW } from '../../../layout/flexBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { css, SerializedStyles } from '@emotion/core';
import { COLORS } from '../../../globalStyle/colors';
import { Rater } from './models';
import { lastCommaFirstLocal } from './module';
import LocalDiv from '../../../localization/LocalDiv';
import { TextDirection } from '../../../localization/textDirection';
import { useAppState } from '../../../core/redux/hooks';
import DeleteRaterModal from './DeleteRaterModal';
import Spacer from '../../../layout/Spacer';
import { isValidDate } from '../../../language/time';
import { doNothing } from '../../../language/module';


const styles = {
  infoRow:      css`
    display: flex;
    flex-direction: row;
    margin: 10px;
  `,
  label:        css`
    font-weight: bold;
  `,
  inviteStatus: css`
  `,
  iconRow:      css`
    width: 100%;
    justify-content: flex-end;
    margin-top: 18px;
  `,
  actionIcon:   css`
    font-size: 18px;
    color: ${COLORS.ICON_GREY};
    cursor: pointer;
  `,
  disabledIcon: css`
    cursor: default;
  `,
};


type Props = {
  className?: string;
  rater: Rater;
  showNames: boolean;
  showRelationship: boolean;
  showLanguage: boolean;
  approval: boolean;
  disableEditBtn: boolean;
  disableDeleteBtn: boolean;
  inviteStatus: React.ReactNode;
  approvalElements: React.ReactNode;
  setRater: (rater: Rater) => void;
  onConfirmDelete: () => void;
  iconStyle?: SerializedStyles;
  approverEmailSent: boolean;
};


/**
 * RaterCard: a component that
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RaterCard: React.FC<Props> = props => {
  const dir                        = useAppState(state => state.locale.textDirection);
  const [deleting, toggleDeleting] = useState<boolean>(false);

  const raterNameTxt = useMemo(() => lastCommaFirstLocal(dir, props.rater) || '---', [dir, props.rater]);

  const approver = useMemo(() =>
    props.approval && props.rater.approvalStatus === 1,
    [props.approval, props.rater.approvalStatus],
  );
  const canEdit = useMemo(() =>
    props.rater.undeliveredEmail
    || (approver && !props.approverEmailSent)
    || (!approver && !isValidDate(props.rater.invitedDate)),
    [props.rater.undeliveredEmail, approver, props.approverEmailSent, props.rater.invitedDate],
  );

  const dismissModal = () => toggleDeleting(false);

  const project = useAppState(state => state.projectSummary);
  const pastDue = useMemo(() => project?.surveyPastDue, [project]);

  return (
    <LocalDiv css={props.className} key={props.rater.raterId}>
      {
        props.showNames && (
          <LocalDiv css={styles.infoRow}>
            <Msg css={styles.label} str={Str.NameColon} />
            <Spacer sizePx={8} />
            {raterNameTxt}
          </LocalDiv>
        )
      }
      <LocalDiv css={styles.infoRow}>
        <Msg css={styles.label} str={Str.EmailColon} />
        <Spacer sizePx={8} />
        {props.rater.email}
      </LocalDiv>
      {
        props.showRelationship && (
      <LocalDiv css={styles.infoRow}>
        <Msg css={styles.label} str={Str.RelationshipColon} />
        <Spacer sizePx={8} />
        {props.rater.levelName}
      </LocalDiv>
        )
      }
      {
        props.showLanguage && (
      <LocalDiv css={styles.infoRow}>
        <Msg css={styles.label} str={Str.LanguageColon} />
        <Spacer sizePx={8} />
        {props.rater.languageNameNative}
      </LocalDiv>
        )
      }
      <LocalDiv css={[FLEX_ROW, styles.infoRow]}>
        <Msg css={styles.label} str={Str.InvitedColon} />
        <Spacer sizePx={8} />
        <LocalDiv css={styles.inviteStatus}>
          {props.inviteStatus}
        </LocalDiv>
      </LocalDiv>
      {props.approval && props.approvalElements}
      {
        !pastDue && (
          <LocalDiv css={[FLEX_ROW, styles.iconRow]} forceDirection={TextDirection.LTR}>
            <Spacer sizePx={50} />
            {
              canEdit && (
                <FontAwesomeIcon
                  icon={faPen}
                  css={[styles.actionIcon, props.iconStyle, props.disableEditBtn && styles.disabledIcon]}
                  onClick={() => props.disableEditBtn ? doNothing() : props.setRater(props.rater)}
                />
              )
            }
            <Spacer sizePx={50} />
            <FontAwesomeIcon
              icon={faTrash}
              css={[styles.actionIcon, props.iconStyle, props.disableEditBtn && styles.disabledIcon]}
              onClick={() => props.disableDeleteBtn ? doNothing() : toggleDeleting(true)}
            />
            <Spacer sizePx={50} />
          </LocalDiv>
        )
      }
      {
        deleting && (
          <DeleteRaterModal
            raterEmail={props.rater.email}
            onClickYes={props.onConfirmDelete}
            onDismiss={dismissModal}
          />
        )
      }
    </LocalDiv>
  );
};

export default RaterCard;
